// only for doctor
import { FontAwesome, MaterialIcons } from "@expo/vector-icons";
import React, { useMemo, useEffect } from "react";
import {
  Dimensions,
  ScrollView,
  Text,
  TouchableOpacity,
  View,
  ActivityIndicator,
  Animated,
} from "react-native";
import { StepHeader, Button, Row, Centered, Input } from "../../../components/all";
import colors from "../../../constants/colors";
import Styles from "../../../constants/styles";
import { useDispatch, useSelector } from "react-redux";
import { addFields } from "../../../reducers/auth/register";
import Collapsible from "react-native-collapsible";
import { useQuery } from "@tanstack/react-query";
import { getProfessionTypes } from "../../../services/shared";
import { getLocalizedProfessionTypeName, extractProfessionTitleAndSubtitle } from "../../../utils/professionUtils";

export default function StepProfession({ navigation, setField, route }) {
  const dispatch = useDispatch();
  const [expandedTypes, setExpandedTypes] = React.useState([]);
  const [searchText, setSearchText] = React.useState("");
  const [animatedValues, setAnimatedValues] = React.useState({});
  const [arrowAnimations, setArrowAnimations] = React.useState({});

  const jobType = route.params?.jobType;
  const { isDev } = useSelector((state) => state.user);
  const { country: userCountry } = useSelector((state) => state.user || {});
  const registerCountry = useSelector((state) => state.auth?.register?.country);
  const country = registerCountry || userCountry || 'MY';

  const [selectedProfessionType, setSelectedProfessionType] = React.useState(null);
  const fromPostJob = route?.name === "PostProfessionScreen";

  // Icon mapping for profession types
  const professionIcons = {
    'a': { icon: 'medical-services', color: '#4B9BFF', bgColor: '#E3F2FF' },  // Doctor
    'b': { icon: 'local-hospital', color: '#10B981', bgColor: '#D1FAE5' },    // Nurse
    'c': { icon: 'medication', color: '#F59E0B', bgColor: '#FEF3C7' },         // Dentist
    'd': { icon: 'healing', color: '#8B5CF6', bgColor: '#EDE9FE' },            // Dental Surgery Assistant
    'e-x': { icon: 'medical-services', color: '#06B6D4', bgColor: '#E0F2FE' }, // Medical Assistant
    'f-x': { icon: 'local-hospital', color: '#0EA5E9', bgColor: '#F0F9FF' },   // Clinic Assistant
    'g-x': { icon: 'school', color: '#7C3AED', bgColor: '#F3E8FF' },           // Research & Education
    'h-x': { icon: 'home', color: '#EC4899', bgColor: '#FCE7F3' },             // Caregiver
    'i-x': { icon: 'settings-input-antenna', color: '#6366F1', bgColor: '#EEF2FF' }, // Radiographer
    'j-x': { icon: 'graphic-eq', color: '#8B5CF6', bgColor: '#F3E8FF' },       // Sonographer
    'k-x': { icon: 'medication', color: '#F59E0B', bgColor: '#FEF3C7' },       // Pharmacist
    'l-x': { icon: 'science', color: '#10B981', bgColor: '#D1FAE5' },          // Laboratory
    'm-x': { icon: 'build', color: '#EF4444', bgColor: '#FEE2E2' },            // Technician
    'n-x': { icon: 'biotech', color: '#F97316', bgColor: '#FFF7ED' },          // Technologist
    'o-x': { icon: 'accessibility', color: '#06B6D4', bgColor: '#E0F2FE' },    // Physiotherapist
    'p-x': { icon: 'psychology', color: '#EC4899', bgColor: '#FCE7F3' },       // Therapist
    'q-x': { icon: 'health-and-safety', color: '#84CC16', bgColor: '#ECFCCB' }, // Allied Health
    'r-x': { icon: 'support-agent', color: '#64748B', bgColor: '#F1F5F9' },    // Healthcare Support
    's-x': { icon: 'business-center', color: '#0891B2', bgColor: '#CFFAFE' },  // Healthcare Management
    't-x': { icon: 'account-balance', color: '#7C3AED', bgColor: '#EDE9FE' },  // Non-Healthcare Management
    'u-x': { icon: 'miscellaneous-services', color: '#CA8A04', bgColor: '#FEF9C3' }, // Non-Healthcare Support
    'default': { icon: 'work-outline', color: colors.primary, bgColor: colors.primary + '10' }
  };

  // Fetch profession types for categorization
  const { data: typesData, isLoading, error, refetch } = useQuery(
    ['professionTypes'],
    () => getProfessionTypes(),
    {
      staleTime: 5 * 60 * 1000, // Cache for 5 minutes
      cacheTime: 10 * 60 * 1000,
      onError: (error) => {
        console.error('Failed to fetch profession types:', error);
      }
    }
  );

  const professionTypes = typesData?.data?.data || [];

  // Organize profession types into hierarchy
  const organizedTypes = useMemo(() => {
    const hierarchy = {};

    professionTypes.forEach(type => {
      const code = type.code;

      // Types ending with -x are treated as parent types without subtypes
      if (code.endsWith('-x')) {
        hierarchy[code] = {
          ...type,
          subtypes: []
        };
      }
      // Top-level types (no dash)
      else if (!code.includes('-')) {
        if (!hierarchy[code]) {
          hierarchy[code] = {
            ...type,
            subtypes: []
          };
        }
      }
      // Second-level types (one dash, but not ending with -x)
      else if (code.split('-').length === 2) {
        const parentCode = code.split('-')[0];
        if (!hierarchy[parentCode]) {
          hierarchy[parentCode] = {
            code: parentCode,
            english_name: 'Other',
            malay_name: 'Lain-lain',
            subtypes: []
          };
        }
        hierarchy[parentCode].subtypes.push(type);
      }
    });

    return hierarchy;
  }, [professionTypes]);

  // Filter types based on search
  const filteredTypes = useMemo(() => {
    if (!searchText) return organizedTypes;

    const filtered = {};
    const lowerSearch = searchText.toLowerCase();

    Object.entries(organizedTypes).forEach(([code, type]) => {
      // Check if parent type matches
      const parentName = getLocalizedProfessionTypeName(type, country, jobType);
      const parentMatches = parentName.toLowerCase().includes(lowerSearch);

      // Check if any subtype matches
      const matchingSubtypes = type.subtypes.filter(subtype => {
        const subtypeName = getLocalizedProfessionTypeName(subtype, country, jobType);
        return subtypeName.toLowerCase().includes(lowerSearch);
      });

      // Include if parent matches or has matching subtypes
      if (parentMatches || matchingSubtypes.length > 0) {
        filtered[code] = {
          ...type,
          subtypes: parentMatches ? type.subtypes : matchingSubtypes
        };
      }
    });

    return filtered;
  }, [organizedTypes, searchText, country]);

  // Initialize animations for each profession type
  useEffect(() => {
    const newAnimatedValues = {};
    const newArrowAnimations = {};

    Object.keys(filteredTypes).forEach((code, index) => {
      if (!animatedValues[code]) {
        newAnimatedValues[code] = new Animated.Value(0);
        newArrowAnimations[code] = new Animated.Value(0);
      }
    });

    if (Object.keys(newAnimatedValues).length > 0) {
      setAnimatedValues(prev => ({ ...prev, ...newAnimatedValues }));
      setArrowAnimations(prev => ({ ...prev, ...newArrowAnimations }));
    }
  }, [filteredTypes]);

  // Animate entrance when data loads
  useEffect(() => {
    if (!isLoading && Object.keys(animatedValues).length > 0) {
      const animations = Object.entries(animatedValues).map(([code, animValue], index) => {
        return Animated.timing(animValue, {
          toValue: 1,
          duration: 400,
          delay: index * 100,
          useNativeDriver: true,
        });
      });

      Animated.parallel(animations).start();
    }
  }, [isLoading, animatedValues]);

  const toggleExpanded = (typeCode) => {
    setExpandedTypes(prev => {
      const isExpanding = !prev.includes(typeCode);

      // Animate arrow rotation for all items
      Object.keys(arrowAnimations).forEach(code => {
        if (arrowAnimations[code]) {
          // Set the target value based on whether this is the item being toggled
          const targetValue = code === typeCode && isExpanding ? 1 : 0;
          Animated.spring(arrowAnimations[code], {
            toValue: targetValue,
            useNativeDriver: true,
            tension: 50,
            friction: 7,
          }).start();
        }
      });

      if (prev.includes(typeCode)) {
        // If clicking on already expanded item, collapse it
        return [];
      }
      // Only allow one expanded item at a time
      return [typeCode];
    });
  };

  const selectProfessionType = (type) => {
    setSelectedProfessionType(type);
  };

  // Show loading state
  if (isLoading) {
    return (
      <View style={{ flex: 1, justifyContent: 'center', alignItems: 'center' }}>
        <ActivityIndicator size="large" color={colors.primary} />
        <Text style={{ marginTop: 16 }}>Loading profession types...</Text>
      </View>
    );
  }

  // Show error state
  if (error) {
    return (
      <View style={{ flex: 1, justifyContent: 'center', alignItems: 'center', padding: 32 }}>
        <Text style={{ fontSize: 16, textAlign: 'center', marginBottom: 16 }}>
          Unable to load profession types. Please check your internet connection and try again.
        </Text>
        <Button onPress={() => refetch()} label="Retry" type="primary" />
      </View>
    );
  }

  return (
    <>
      <ScrollView
        style={{
          ...Styles.webSmallContainer,
          marginTop: fromPostJob ? 48 : 80,
        }}
      >
        <StepHeader
          label={`Select Profession Type`}
          icon={<FontAwesome name="briefcase" size={24} color="black" />}
          subtitle={fromPostJob ? `Select the profession type you are hiring for` : `Select your medical profession type`}
        />
        <View style={{
          marginHorizontal: 16,
          marginTop: 32,
        }}>
          <Input
            placeholder="Search Profession Type"
            onChangeText={(text) => {
              setSearchText(text);
              // Auto-expand when searching
              if (text.length > 0) {
                setExpandedTypes(Object.keys(filteredTypes));
              } else {
                setExpandedTypes([]);
              }
            }}
          />
        </View>
        <View
          style={{
            width: Dimensions.get("window").width,
            paddingHorizontal: 16,
            marginTop: 16,
          }}
        >
          {Object.entries(filteredTypes).map(([parentCode, parentType], index) => {
            const hasSubtypes = parentType.subtypes.length > 0;
            const isExpanded = hasSubtypes && (expandedTypes.includes(parentCode) || searchText.length > 0);
            const isSelected = !hasSubtypes && selectedProfessionType?.code === parentCode;
            const iconConfig = professionIcons[parentCode] || professionIcons.default;
            const animValue = animatedValues[parentCode] || new Animated.Value(1);
            const arrowAnim = arrowAnimations[parentCode] || new Animated.Value(0);

            return (
              <Animated.View
                key={parentCode}
                style={{
                  marginBottom: 16,
                  opacity: animValue,
                  transform: [
                    {
                      translateY: animValue.interpolate({
                        inputRange: [0, 1],
                        outputRange: [20, 0],
                      }),
                    },
                  ],
                }}
              >
                {/* Parent Type (Doctor, Nurse, etc.) */}
                <TouchableOpacity
                  onPress={() => {
                    if (hasSubtypes) {
                      toggleExpanded(parentCode);
                    } else {
                      // Direct selection for types without subtypes
                      selectProfessionType(parentType);
                    }
                  }}
                  activeOpacity={0.7}
                  style={{
                    backgroundColor: isExpanded || isSelected ? iconConfig.bgColor : "white",
                    paddingHorizontal: 16,
                    paddingVertical: 12,
                    borderRadius: 20,
                    flexDirection: 'row',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    borderWidth: 2,
                    borderColor: isExpanded || isSelected ? iconConfig.color : "#F3F4F6",
                    shadowColor: "#000",
                    shadowOffset: {
                      width: 0,
                      height: 4,
                    },
                    shadowOpacity: isExpanded || isSelected ? 0.15 : 0.08,
                    shadowRadius: 12,
                    elevation: 3,
                  }}
                >
                  <View style={{ flexDirection: 'row', alignItems: 'center', flex: 1 }}>
                    <View
                      style={{
                        width: 40,
                        height: 40,
                        backgroundColor: isExpanded || isSelected ? iconConfig.color : iconConfig.bgColor,
                        borderRadius: 24,
                        marginRight: 16,
                        justifyContent: "center",
                        alignItems: "center"
                      }}
                    >
                      <MaterialIcons
                        name={iconConfig.icon}
                        size={20}
                        color={isExpanded || isSelected ? "white" : iconConfig.color}
                      />
                    </View>
                    <View style={{ flex: 1 }}>
                      {(() => {
                        const professionName = getLocalizedProfessionTypeName(parentType, country, jobType);
                        const { title, subtitle } = extractProfessionTitleAndSubtitle(professionName);
                        return (
                          <>
                            <Text style={{
                              fontSize: 18,
                              fontWeight: "700",
                              color: isExpanded || isSelected ? iconConfig.color : "#1F2937",
                              letterSpacing: -0.5,
                              textTransform: 'capitalize',
                            }}>
                              {title}
                            </Text>
                            {subtitle && (
                              <Text style={{
                                fontSize: 14,
                                fontWeight: "500",
                                color: (isExpanded || isSelected) ? iconConfig.color + 'CC' : "#6B7280",
                                letterSpacing: -0.2,
                                marginTop: 2,
                                textTransform: 'capitalize',
                              }}>
                                {subtitle}
                              </Text>
                            )}
                          </>
                        );
                      })()}
                      {hasSubtypes && (
                        <Text style={{
                          fontSize: 12,
                          color: isExpanded ? "#4B5563" : "#6B7280",
                          marginTop: 2,
                          letterSpacing: -0.1,
                        }}>
                          {parentType.subtypes.length} specialization{parentType.subtypes.length !== 1 ? 's' : ''}
                        </Text>
                      )}
                    </View>
                  </View>
                  {hasSubtypes ? (
                    <Animated.View
                      style={{
                        transform: [
                          {
                            rotate: arrowAnim.interpolate({
                              inputRange: [0, 1],
                              outputRange: ['0deg', '90deg'],
                            }),
                          },
                        ],
                      }}
                    >
                      <MaterialIcons
                        name="chevron-right"
                        size={24}
                        color={isExpanded ? iconConfig.color : "#9CA3AF"}
                      />
                    </Animated.View>
                  ) : (
                    // Show checkmark for selectable parent types
                    <View
                      style={{
                        width: 24,
                        height: 24,
                        borderRadius: 12,
                        borderWidth: 2,
                        borderColor: isSelected ? iconConfig.color : "#D1D5DB",
                        backgroundColor: isSelected ? iconConfig.color : "white",
                        justifyContent: 'center',
                        alignItems: 'center',
                      }}
                    >
                      {isSelected && (
                        <MaterialIcons
                          name="check"
                          size={16}
                          color="white"
                        />
                      )}
                    </View>
                  )}
                </TouchableOpacity>

                {/* Subtypes (GP, Specialist, etc.) - Only show if there are subtypes */}
                {hasSubtypes && (
                  <Collapsible collapsed={!isExpanded}>
                    <View style={{ marginTop: 12, marginLeft: 0 }}>
                      {parentType.subtypes.map((subtype, subtypeIndex) => {
                      const isSelected = selectedProfessionType?.code === subtype.code;
                      return (
                        <Animated.View
                          key={subtype.code}
                          style={{
                            opacity: isExpanded ? 1 : 0,
                            transform: [
                              {
                                translateX: isExpanded ? 0 : -20,
                              },
                              {
                                scale: isSelected ? 0.98 : 1,
                              },
                            ],
                          }}
                        >
                          <TouchableOpacity
                            onPress={() => selectProfessionType(subtype)}
                            activeOpacity={0.7}
                            style={{
                              backgroundColor: isSelected
                                ? iconConfig.color + '15'
                                : "white",
                              paddingHorizontal: 20,
                              paddingVertical: 12,
                              marginBottom: 8,
                              borderRadius: 16,
                              borderWidth: 2,
                              borderColor: isSelected
                                ? iconConfig.color
                                : "#eee",
                              flexDirection: 'row',
                              alignItems: 'center',
                              shadowColor: "#000",
                              shadowOffset: {
                                width: 0,
                                height: 2,
                              },
                              shadowRadius: 6,
                            }}
                          >
                            <View
                              style={{
                                width: 16,
                                height: 16,
                                borderRadius: 12,
                                borderWidth: 2,
                                borderColor: isSelected ? iconConfig.color : "#D1D5DB",
                                backgroundColor: isSelected ? iconConfig.color : "white",
                                marginRight: 12,
                                justifyContent: 'center',
                                alignItems: 'center',
                              }}
                            >
                              {isSelected && (
                                <MaterialIcons
                                  name="check"
                                  size={12}
                                  color="white"
                                />
                              )}
                            </View>
                            {(() => {
                              const subtypeName = getLocalizedProfessionTypeName(subtype, country, jobType);
                              const { title: subtypeTitle, subtitle: subtypeSubtitle } = extractProfessionTitleAndSubtitle(subtypeName);
                              return (
                                <View style={{ flex: 1 }}>
                                  <Text style={{
                                    fontSize: 15,
                                    color: isSelected
                                      ? iconConfig.color
                                      : "#374151",
                                    fontWeight: isSelected
                                      ? "600"
                                      : "400",
                                    letterSpacing: -0.2,
                                  }}>
                                    {subtypeTitle}
                                  </Text>
                                  {subtypeSubtitle && (
                                    <Text style={{
                                      fontSize: 12,
                                      color: isSelected
                                        ? iconConfig.color + 'CC'
                                        : "#6B7280",
                                      fontWeight: "400",
                                      letterSpacing: -0.1,
                                      marginTop: 2,
                                    }}>
                                      {subtypeSubtitle}
                                    </Text>
                                  )}
                                </View>
                              );
                            })()}
                          </TouchableOpacity>
                        </Animated.View>
                      );
                      })}
                    </View>
                  </Collapsible>
                )}
              </Animated.View>
            );
          })}
        </View>
        <View style={{ marginBottom: 48 }} />
      </ScrollView>
      <Centered
        style={{
          position: "fixed",
          bottom: 32,
          backgroundColor: "white",
          width: "100%",
          paddingHorizontal: 24,
        }}
      >
        <Row style={{ marginBottom: 24 }}>
          <Button
            label="Back"
            type={"secondary"}
            onPress={() => {
              if (!isDev) {
                navigation.pop(2);
              } else {
                navigation.goBack();
              }
            }}
          />
          <Button
            label="Next"
            type={"primary"}
            onPress={() => {
              if (selectedProfessionType) {
                console.log('Selected profession type:', selectedProfessionType);
                console.log('From post job:', fromPostJob);

                if (fromPostJob) {
                  // Navigate to PostJobCategoryScreen for job posting
                  navigation.navigate("PostJobCategoryScreen", {
                    profession_type_code: selectedProfessionType.code,
                    profession_type: selectedProfessionType,
                    jobType: jobType,
                  });
                } else {
                  // Navigate to CategoryScreen for doctor registration
                  let fields = {
                    profession_type_code: selectedProfessionType.code,
                    profession_type: selectedProfessionType,
                    // Set isSpecialist based on the selected type
                    isSpecialist: selectedProfessionType.code.includes('-b'), // Ends with -b means specialist
                  };
                  dispatch(addFields(fields));
                  // Also pass as navigation params to ensure CategoryScreen receives them
                  navigation.navigate("CategoryScreen", {
                    profession_type_code: selectedProfessionType.code,
                    profession_type: selectedProfessionType,
                    jobType: jobType,
                  });
                }
              } else {
                alert("Please select a profession type");
              }
            }}
          />
        </Row>
      </Centered>
    </>
  );
}

const styles = {
  category: {
    height: 64,
    justifyContent: "center",
    margin: 6,
    marginTop: 10,
    paddingHorizontal: 24,
    alignItems: "center",
    borderWidth: 1,
    borderRadius: 18,
    width: "80%",
  },
};